<template>
    <DSWrapper :wrap="wrap" :role="type === 'radio' ? 'radiogroup' : 'group'">
        <label
            v-for="item in items"
            :key="item.key"
            :class="{
                'ds-input-checkbox': type === 'checkbox',
                'ds-input-radio': type === 'radio',
                'ds-input-radio--boxed': type === 'radio' && boxed,
                'ds-switch': type === 'switch',
                'is-checked': itemIsChecked(item),
                'is-disabled': item.disabled,
                'is-focused': isFocused,
                'has-error': error,
                ...item.class,
            }"
            @click="onLabelClick($event, item)"
        >
            <template v-if="type === 'checkbox'">
                <input
                    :id="item.id"
                    v-model="checked"
                    class="sr-only"
                    type="checkbox"
                    tabindex="0"
                    :name="item.name"
                    :disabled="item.disabled"
                    :value="item.value"
                    @blur="isFocused = false"
                    @focus="isFocused = true"
                />
                <slot name="label" :option="item">{{ item.label }}</slot>
            </template>

            <template v-else-if="type === 'switch'">
                <input
                    :id="item.id"
                    v-model="checked"
                    class="sr-only"
                    type="checkbox"
                    role="switch"
                    :name="item.name"
                    :value="item.value"
                    @blur="isFocused = false"
                    @focus="isFocused = true"
                />
                <span class="ds-switch__slider">
                    <slot name="label" :option="item">{{ item.label }}</slot>
                </span>
            </template>

            <template v-else-if="type === 'radio'">
                <input
                    :id="item.id"
                    v-model="checked"
                    class="sr-only"
                    type="radio"
                    :name="item.name"
                    :disabled="item.disabled"
                    :value="item.value"
                    @blur="isFocused = false"
                    @focus="isFocused = true"
                />
                <slot name="label" :option="item">{{ item.label }}</slot>
            </template>
        </label>
        <div
            v-if="showValidationError && error"
            class="text-after error"
        >
            {{ error }}
        </div>
    </DSWrapper>
</template>
<script setup>
import { computed, ref, reactive, isProxy } from 'vue';
import { isObject } from '~dsc/plugins/dsc/object';

const props = defineProps({
    type: {
        type: String,
        default: 'checkbox',
    },
    label: {
        type: String,
        default: '',
    },
    options: {
        type: [Array, Object],
        default: () => [],
    },
    modelValue: {
        type: null,
        required: true,
    },
    name: {
        type: String,
        default: null,
    },
    error: {
        type: String,
        default: '',
    },
    wrap: {
        type: Boolean,
        default: null,
    },
    boxed: {
        type: Boolean,
        default: false,
    },
    showValidationError: {
        type: Boolean,
        default: false,
    },
    bypassInputFocus: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([ 'update:modelValue' ]);

const isFocused = ref(false);

const items = computed(() => {
    if (!props.options?.length && props.label) {
        return [{
            label: props.label,
            name: props.name,
        }];
    }

    const options = isObject(props.options) ? Object.entries(props.options).map(([key, value]) => ({ value: key, label: value })) : props.options;

    return options.map(x => {
        const o = isObject(x) ? x : { value: x };

        return {
            label: o.value,
            key: o.value,
            name: props.name,
            ...o,
        };
    });
});
const wrap = computed(() => props.wrap ?? items.value.length > 1);

const checked = computed({
    get: () => props.modelValue,
    set: newValue => emit('update:modelValue', newValue),
});

function itemIsChecked(item) {
    if (props.type === 'radio') {
        return isProxy(checked.value) ? checked.value === reactive(item.value) : checked.value === item.value;
    }

    return Array.isArray(checked.value) ? checked.value.includes(item.value) : checked.value;
}

const onLabelClick = (e, item) => {
    if (props.bypassInputFocus) {
        if (item.disabled) return;

        e.preventDefault();

        if (props.type === 'radio') return emit('update:modelValue', item.value);
        if (!Array.isArray(props.modelValue)) return emit('update:modelValue', !props.modelValue);

        const newSelection = [...props.modelValue];
        const index = newSelection.indexOf(item.value);

        if (index < 0) {
            newSelection.push(item.value);
        } else {
            newSelection.splice(index, 1);
        }

        emit('update:modelValue', newSelection);
    }
};
</script>
